export const SITE_ORIGIN = 'https://vizmarkets.com';

const INDEX_ROBOTS = 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';
const PRIVATE_ROBOTS = 'noindex, follow, noarchive';

export const SEO_ROUTES = Object.freeze({
  overview: Object.freeze({
    path: '/',
    title: 'Indian Stock Market Map & Screener | VizMarkets',
    description: 'Explore Indian equities with a visual market heatmap, factual stock screener, regulatory news, watchlists and paper-trading tools. Data may be delayed.',
    indexable: true,
  }),
  map: Object.freeze({
    path: '/market-map',
    title: 'Indian Stock Market Heatmap & Sector Map | VizMarkets',
    description: 'See market breadth, sector rotation and leading Indian shares in an interactive market-cap-weighted heatmap. Data may be delayed.',
    indexable: true,
  }),
  screener: Object.freeze({
    path: '/stock-screener',
    title: 'Indian Stock Screener for NSE Shares | VizMarkets',
    description: 'Explore a factual Indian stock screener with company, sector, market-cap, valuation and price-performance filters for a tracked NSE universe.',
    indexable: true,
  }),
  news: Object.freeze({
    path: '/market-news',
    title: 'SEBI, RBI & Indian Market News | VizMarkets',
    description: 'Follow Indian market coverage, SEBI and RBI bulletins, and government policy updates with clearly identified publishers and official sources.',
    indexable: true,
  }),
  research: Object.freeze({
    path: '/research-services',
    title: 'Indian Market Research Services Roadmap | VizMarkets',
    description: 'Preview the VizMarkets research service roadmap, including market briefs, stock analysis and IPO coverage with explicit regulatory gates.',
    indexable: true,
  }),
  learn: Object.freeze({
    path: '/investor-education',
    title: 'Indian Investor Education | VizMarkets',
    description: 'Learn market structure, valuation, risk and portfolio principles through educational modules and clearly labelled historical examples.',
    indexable: true,
  }),
  compliance: Object.freeze({
    path: '/trust-and-compliance',
    title: 'Trust, Data & Regulatory Compliance | VizMarkets',
    description: 'Understand how VizMarkets labels market data, separates education from regulated advice, and gates research services for responsible launch.',
    indexable: true,
  }),
  support: Object.freeze({
    path: '/support-us',
    title: 'Support VizMarkets | VizMarkets',
    description: 'Help keep core market visualization tools free and VizMarkets improving. Make a voluntary contribution through your preferred payment provider.',
    indexable: true,
  }),
  watchlist: Object.freeze({
    path: '/watchlist',
    title: 'Your Watchlist & Price Alerts | VizMarkets',
    description: 'Review the companies and informational price thresholds saved in your local VizMarkets workspace.',
    indexable: false,
  }),
  portfolio: Object.freeze({
    path: '/paper-portfolio',
    title: 'Your Paper Portfolio | VizMarkets',
    description: 'Review your browser-based VizMarkets paper portfolio simulation. No orders are placed or routed.',
    indexable: false,
  }),
  alerts: Object.freeze({
    path: '/price-alerts',
    title: 'Your Price Alerts | VizMarkets',
    description: 'Review the informational price thresholds stored in your local VizMarkets workspace.',
    indexable: false,
  }),
});

export const KNOWN_ROUTE_PATHS = Object.freeze(Object.values(SEO_ROUTES).map((route) => route.path));
export const INDEXABLE_ROUTE_PATHS = Object.freeze(Object.values(SEO_ROUTES).filter((route) => route.indexable).map((route) => route.path));

const VIEW_BY_PATH = new Map(Object.entries(SEO_ROUTES).map(([view, route]) => [route.path, view]));

function normalizePath(pathname = '/') {
  const path = pathname.split('?')[0].split('#')[0] || '/';
  return path.length > 1 ? path.replace(/\/+$/, '') : path;
}

export function getViewForPath(pathname) {
  return VIEW_BY_PATH.get(normalizePath(pathname)) || null;
}

export function getRouteForView(view) {
  return SEO_ROUTES[view] || SEO_ROUTES.overview;
}

export function getRouteForPath(pathname) {
  const view = getViewForPath(pathname);
  return view ? SEO_ROUTES[view] : null;
}

export function getRoutePath(view) {
  return getRouteForView(view).path;
}

export function getCanonicalUrl(view) {
  return `${SITE_ORIGIN}${getRoutePath(view)}`;
}

export function getRobotsDirective(route) {
  return route.indexable ? INDEX_ROBOTS : PRIVATE_ROBOTS;
}

function setMetaContent(selector, content) {
  const element = document.head.querySelector(selector);
  if (element) element.setAttribute('content', content);
}

export function applySeoForView(view) {
  if (typeof document === 'undefined') return;
  const route = getRouteForView(view);
  const canonicalUrl = `${SITE_ORIGIN}${route.path}`;

  document.title = route.title;
  document.documentElement.lang = 'en-IN';
  setMetaContent('meta[name="description"]', route.description);
  setMetaContent('meta[name="robots"]', getRobotsDirective(route));
  setMetaContent('meta[name="googlebot"]', getRobotsDirective(route));
  setMetaContent('meta[property="og:title"]', route.title);
  setMetaContent('meta[property="og:description"]', route.description);
  setMetaContent('meta[property="og:url"]', canonicalUrl);
  setMetaContent('meta[name="twitter:title"]', route.title);
  setMetaContent('meta[name="twitter:description"]', route.description);

  const canonical = document.head.querySelector('link[rel="canonical"]');
  if (canonical) canonical.setAttribute('href', canonicalUrl);
}

function escapeHtml(value) {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('"', '&quot;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;');
}

function replaceMeta(html, marker, name, value, attribute = 'name') {
  const tag = `<meta data-seo="${marker}" ${attribute}="${name}" content="${escapeHtml(value)}" />`;
  return html.replace(new RegExp(`<meta(?=[^>]*data-seo=["']${marker}["'])[^>]*>`, 'i'), tag);
}

export function renderSeoDocument(template, route) {
  const canonicalUrl = `${SITE_ORIGIN}${route.path}`;
  let html = template.replace(
    /<title(?=[^>]*data-seo=["']title["'])[^>]*>[\s\S]*?<\/title>/i,
    `<title data-seo="title">${escapeHtml(route.title)}</title>`,
  );
  html = replaceMeta(html, 'description', 'description', route.description);
  html = replaceMeta(html, 'robots', 'robots', getRobotsDirective(route));
  html = replaceMeta(html, 'googlebot', 'googlebot', getRobotsDirective(route));
  html = replaceMeta(html, 'og-title', 'og:title', route.title, 'property');
  html = replaceMeta(html, 'og-description', 'og:description', route.description, 'property');
  html = replaceMeta(html, 'og-url', 'og:url', canonicalUrl, 'property');
  html = replaceMeta(html, 'twitter-title', 'twitter:title', route.title);
  html = replaceMeta(html, 'twitter-description', 'twitter:description', route.description);
  return html.replace(
    /<link(?=[^>]*data-seo=["']canonical["'])[^>]*>/i,
    `<link data-seo="canonical" rel="canonical" href="${escapeHtml(canonicalUrl)}" />`,
  );
}
